import { useCallback, useState } from 'react';
import { addScope, syncBetScopes, syncCycleScopes, updateScope } from '../../utils/shapeUpApi';

/**
 * Scope and sync handlers shared by the cycle stack and the bet detail page,
 * so a scope behaves the same wherever its dot is dragged.
 *
 * @param {Function} reload - Refetches whichever read model the caller renders.
 * @param {Function} applyScopeUpdate - Applies an optimistic patch to local state.
 * @param {Function} [onSynced] - Fired after a successful Jira sync, so the page
 *   can refresh things the hill read model does not carry (e.g. comment threads).
 */
const useScopeActions = ({ reload, applyScopeUpdate, onSynced }) => {
  const [toast, setToast] = useState(null);
  const [syncingBetId, setSyncingBetId] = useState(null);
  const [syncingCycle, setSyncingCycle] = useState(false);
  // The scope whose move is waiting for an explanation, if any.
  const [noteRequest, setNoteRequest] = useState(null);

  const patchScope = useCallback(async (scope, updates, successMessage) => {
    // Reflect the change immediately, then reconcile with the server response.
    applyScopeUpdate(scope.id, updates);

    try {
      const updated = await updateScope(scope.id, updates);
      await reload({ quiet: true });
      if (successMessage) setToast({ severity: 'success', message: successMessage });
      return updated;
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not update the scope.' });
      reload({ quiet: true });
      return null;
    }
  }, [applyScopeUpdate, reload]);

  // The move is saved first and the reason asked for afterwards, so the question is
  // never in the way of the gesture and can be ignored entirely.
  const handleMoveScope = useCallback(async (scope, position) => {
    const updated = await patchScope(scope, { hillPositionOverride: position, positionSource: 'override', position });
    // The dot moved either way; the status may not have. When Jira could not
    // follow the drag, the server says why, and that is worth surfacing over the
    // silent success so the position and the board are not quietly out of step.
    if (updated?.statusWarning) {
      setToast({ severity: 'warning', message: updated.statusWarning });
    }
    setNoteRequest({ scope, position });
  }, [patchScope]);

  const saveMoveNote = useCallback(async ({ scope, position, restating }, note) => {
    setNoteRequest(null);

    // Restating a dot that is already placed by hand is a fresh judgement, so it
    // earns its own point on the trail. Restating one that follows Jira only adds
    // the reason: pinning it would quietly stop the status from moving it again,
    // which is not what agreeing with the position means.
    const updates = restating && scope.positionSource === 'override'
      ? { hillPositionOverride: position, note }
      : { note };

    try {
      await updateScope(scope.id, updates);
      applyScopeUpdate(scope.id, { lastJudgmentNote: note });
      await reload({ quiet: true });
      setToast({ severity: 'success', message: `Noted against ${scope.jiraKey}` });
    } catch (err) {
      setToast({
        severity: 'error',
        message: err.response?.data?.message || 'The position was saved, but the note was not.',
      });
    }
  }, [applyScopeUpdate, reload]);

  // Re-affirming a position without moving it: "still here, and here is why".
  const handleRestate = useCallback((scope) => {
    setNoteRequest({ scope, position: scope.position ?? 0, restating: true });
  }, []);

  const handleClearOverride = useCallback((scope) => patchScope(
    scope,
    { hillPositionOverride: null },
    `${scope.jiraKey} now follows its Jira status`
  ), [patchScope]);

  const handleTogglePark = useCallback((scope) => patchScope(
    scope,
    { isParked: !scope.isParked },
    scope.isParked ? `Unparked ${scope.jiraKey}` : `Parked ${scope.jiraKey}`
  ), [patchScope]);

  const handleExcludeScope = useCallback((scope) => patchScope(
    scope,
    { isExcluded: true },
    `Removed ${scope.jiraKey} from the hill`
  ), [patchScope]);

  // Hammering asks for a reason, since a cut with no recorded reason is the part
  // a retrospective cannot reconstruct. Skipping the prompt still hammers it.
  const handleHammerScope = useCallback((scope) => {
    if (scope.isHammered) {
      return patchScope(scope, { isHammered: false }, `${scope.jiraKey} is back in the bet`);
    }

    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Hammering ${scope.jiraKey} cuts it to fit the appetite. What was trimmed away, or why?`,
      ''
    );
    if (reason === null) return undefined;

    return patchScope(
      scope,
      { isHammered: true, hammeredReason: reason.trim() || null },
      `Hammered ${scope.jiraKey}`
    );
  }, [patchScope]);

  // Renames the scope's Jira issue, so it is not optimistic: if Jira refuses,
  // the old name is still the truth.
  const handleRenameScope = useCallback(async (scope, summary) => {
    const trimmed = summary.trim();
    if (!trimmed || trimmed === scope.jiraSummary) return;

    try {
      await updateScope(scope.id, { summary: trimmed });
      await reload({ quiet: true });
      setToast({ severity: 'success', message: `Renamed ${scope.jiraKey} in Jira` });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || `Could not rename ${scope.jiraKey}.` });
    }
  }, [reload]);

  // Manual mode only: the status is picked from a dropdown rather than read from
  // Jira, and moving it lets go of any hand-placed override so the dot follows the
  // new status band. The server ignores both on a Jira-backed scope.
  const handleChangeStatus = useCallback((scope, status) => patchScope(
    scope,
    { status },
    `Set ${scope.jiraSummary || 'the scope'} to ${status}`
  ), [patchScope]);

  const handleChangeAssignee = useCallback((scope, assigneeName) => patchScope(
    scope,
    { assigneeName: assigneeName ? assigneeName.trim() : null }
  ), [patchScope]);

  const handleSync = useCallback(async (bet) => {
    setSyncingBetId(bet.id);
    try {
      const result = await syncBetScopes(bet.id);
      await reload({ quiet: true });
      // The same sync also pulls Jira comments in; nudge the threads to refetch.
      if (onSynced) onSynced();
      setToast({ severity: 'success', message: result.message });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not sync from Jira.' });
    } finally {
      setSyncingBetId(null);
    }
  }, [reload, onSynced]);

  // The whole-cycle counterpart to handleSync: one request syncs every
  // Jira-linked bet, so the header's "synced X ago" refreshes in a single pass
  // rather than a click per card.
  const handleSyncCycle = useCallback(async (cycleId) => {
    if (!cycleId) return;
    setSyncingCycle(true);
    try {
      const result = await syncCycleScopes(cycleId);
      await reload({ quiet: true });
      if (onSynced) onSynced();
      setToast({ severity: 'success', message: result.message || 'Synced from Jira.' });
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'Could not sync from Jira.' });
    } finally {
      setSyncingCycle(false);
    }
  }, [reload, onSynced]);

  const handleAddScope = useCallback(async (betId, jiraKey) => {
    await addScope(betId, jiraKey);
    await reload({ quiet: true });
    setToast({ severity: 'success', message: `Added ${jiraKey}` });
  }, [reload]);

  return {
    toast,
    setToast,
    syncingBetId,
    setSyncingBetId,
    noteRequest,
    saveMoveNote,
    dismissNote: () => setNoteRequest(null),
    handleMoveScope,
    handleRestate,
    handleClearOverride,
    handleTogglePark,
    handleExcludeScope,
    handleRenameScope,
    handleHammerScope,
    handleChangeStatus,
    handleChangeAssignee,
    handleSync,
    handleSyncCycle,
    syncingCycle,
    handleAddScope,
  };
};

export default useScopeActions;
